import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Input, Space, Tag, Tooltip, Modal, Divider, Badge, Upload, Collapse } from 'antd';
import {
  SearchOutlined, EditOutlined, DeleteOutlined, PlusOutlined,
  AppstoreOutlined, EyeOutlined, TagOutlined, ShoppingOutlined,
  DollarOutlined, InboxOutlined, StarOutlined, PercentageOutlined,
  LeftOutlined, RightOutlined, UploadOutlined, DownloadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import BASEURL from '../API/Config';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewProduct, setViewProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const handleDownloadTemplate = async () => {
    try {
      Swal.fire({
        title: 'Generating Template...',
        text: 'Please wait while the Excel catalog is compiled.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const res = await fetch(`${BASEURL}/subcategory/exportExcel`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error('Failed to download template from server');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_catalog_bulk.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      Swal.close();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to download product template. Please try again.', 'error');
    }
  };

  const handleBulkUpload = async () => {
    if (!file) {
      Swal.fire('Warning', 'Please select an Excel file first.', 'warning');
      return;
    }

    setUploadLoading(true);
    setUploadReport(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${BASEURL}/subcategory/uploadExcel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const data = await res.json();
      if (res.ok && data.success) {
        Swal.fire('Success', data.message || 'Excel uploaded successfully!', 'success');
        setUploadReport(data.report);
        setFile(null);
        
        // Refresh product list
        const prodRes = await fetch(`${BASEURL}/Products`);
        const prodData = await prodRes.json();
        setProducts(Array.isArray(prodData.products) ? prodData.products : []);
      } else {
        Swal.fire('Error', data.message || 'Failed to upload spreadsheet.', 'error');
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      Swal.fire('Error', 'An error occurred during upload.', 'error');
    } finally {
      setUploadLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [catRes, prodRes, unitRes] = await Promise.all([
          fetch(`${BASEURL}/pcategory`),
          fetch(`${BASEURL}/Products`),
          fetch(`${BASEURL}/units`),
        ]);
        const catData = await catRes.json();
        const prodData = await prodRes.json();
        const unitData = await unitRes.json();
        setCategories(Array.isArray(catData) ? catData : []);
        setProducts(Array.isArray(prodData.products) ? prodData.products : []);
        setUnits(Array.isArray(unitData) ? unitData : []);
      } catch { Swal.fire('Error', 'Failed to load products.', 'error'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const getCategoryName = (id) => {
    if (typeof id === 'object') return id?.title || 'N/A';
    return categories.find(c => c._id.toString() === id?.toString())?.title || 'N/A';
  };
  const getUnitName = (id) => {
    if (typeof id === 'object') return id?.title || '—';
    return units.find(u => u._id.toString() === id?.toString())?.title || '—';
  };

  const openView = (product) => {
    setViewProduct(product);
    setActiveImg(0);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Product?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e53935', confirmButtonText: 'Yes, Delete',
    }).then(async (result) => {
      if (result.isConfirmed) {
        const res = await fetch(`${BASEURL}/Products/deleteproduct/${id}`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          Swal.fire('Deleted!', 'Product removed.', 'success');
          setProducts(p => p.filter(prod => prod._id !== id));
        }
      }
    });
  };

  const filtered = products.filter(p =>
    p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const discount = (p) => {
    const v = p.variants?.[0];
    const current = v?.CurrentPrice || p.CurrentPrice;
    const offer = v?.offerPrice || p.offerPrice;
    return current && offer && offer < current
      ? Math.round((1 - offer / current) * 100) : 0;
  };

  const columns = [
    { title: "S.No", key: "idx", width: 55, render: (_, __, i) => <span style={{ color: "#6b7280" }}>{i + 1}</span> },
    {
      title: 'PRODUCT', key: 'product',
      render: (r) => (
        <Space>
          {r.images?.[0]?.url ? (
            <img src={`${BASEURL}${r.images[0].url}`} alt="product"
              style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 8, border: '1px solid #e0e0e0' }} />
          ) : (
            <div style={{ width: 42, height: 42, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
              <AppstoreOutlined />
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600, color: '#212121', fontSize: 13, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{getCategoryName(r.Category)}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'VARIANT / PRICE', key: 'price',
      render: (r) => {
        const unitTitle = getUnitName(r.unit || r.variants?.[0]?.unit);
        if (r.variants && r.variants.length > 0) {
          return (
            <div style={{ minWidth: 140 }}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                {r.variants.slice(0, 3).map((v, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9fafb', padding: '2px 8px', borderRadius: 4, border: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{v.measurementUnit} {getUnitName(v.unit)}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                      ₹{v.offerPrice > 0 ? v.offerPrice : v.CurrentPrice}
                    </span>
                  </div>
                ))}
                {r.variants.length > 3 && (
                  <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', fontStyle: 'italic' }}>
                    + {r.variants.length - 3} more variants
                  </div>
                )}
              </Space>
            </div>
          );
        }
        return (
          <div style={{ padding: '2px 8px' }}>
            <div style={{ fontWeight: 700, color: '#212121', fontSize: 14 }}>₹{r.offerPrice > 0 ? r.offerPrice : r.CurrentPrice}</div>
            {r.offerPrice > 0 && r.CurrentPrice > 0 && r.offerPrice < r.CurrentPrice && (
              <div style={{ fontSize: 11, color: '#9ca3af', textDecoration: 'line-through' }}>₹{r.CurrentPrice}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'GST', key: 'gst',
      render: (r) => {
        const gst = r.gst || r.variants?.[0]?.gst || 0;
        return (
          <Space direction="vertical" size={2}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Tag color="blue" style={{ borderRadius: 10, margin: 0 }}>{gst}%</Tag>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'ACTIONS', key: 'actions', width: 130,
      render: (r) => (
        <Space>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => openView(r)}
              style={{ color: '#059669', borderColor: '#059669', borderRadius: 4 }} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => navigate('/admin/add-product', { state: { product: r, isEdit: true } })}
              style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
              style={{ borderRadius: 4 }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  /* ─── View Detail Modal Images ─── */
  const images = viewProduct?.images || [];
  const prevImg = () => setActiveImg(i => (i - 1 + images.length) % images.length);
  const nextImg = () => setActiveImg(i => (i + 1) % images.length);

  return (
    <div>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Catalog</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Product List</h2>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            type="dashed"
            icon={<UploadOutlined />}
            onClick={() => {
              setFile(null);
              setUploadReport(null);
              setShowUploadModal(true);
            }}
            style={{ color: '#4f46e5', borderColor: '#4f46e5', borderRadius: 4, height: 38, fontWeight: 600 }}
          >
            Bulk Excel Operations
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/admin/add-product')}
            style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
            Add Product
          </Button>
        </div>
      </div>

      {/* Table Card */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <Input allowClear prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search product..."
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 320, borderRadius: 6, height: 38 }} />
          
          <div style={{ display: 'flex', gap: 24, marginLeft: 'auto' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Total Products</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#212121' }}>{filtered.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Total Variants</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#059669' }}>
                {filtered.reduce((acc, p) => acc + (p.variants?.length || 1), 0)}
              </div>
            </div>
          </div>
        </div>
        <Table 
          columns={columns} 
          dataSource={filtered} 
          rowKey="_id" 
          loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"], showTotal: (t) => `${t} products` }} 
          size="middle"
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ margin: '0 0 0 50px', padding: '12px 24px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <ShoppingOutlined style={{ color: '#2874f0', fontSize: 16 }} />
                  <span style={{ fontWeight: 700, color: '#334155', fontSize: 14 }}>Inventory Variants Breakdown</span>
                </div>
                <Table
                  dataSource={record.variants || []}
                  rowKey={(v, i) => `${record._id}-var-${i}`}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'UNIT',
                      key: 'unit',
                      render: (v) => <Tag style={{ borderRadius: 4, fontWeight: 600 }}>{getUnitName(v.unit)}</Tag>
                    },
                    {
                      title: 'MEASUREMENT',
                      dataIndex: 'measurementUnit',
                      key: 'measure',
                      render: (text) => <span style={{ color: '#475569', fontWeight: 500 }}>{text}</span>
                    },
                    {
                      title: 'PRICE',
                      key: 'price',
                      align: 'right',
                      render: (v) => (
                        <div>
                          <div style={{ fontWeight: 700, color: '#1e293b' }}>₹{v.offerPrice > 0 ? v.offerPrice : v.CurrentPrice}</div>
                          {v.offerPrice > 0 && v.offerPrice < v.CurrentPrice && (
                            <div style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'line-through' }}>₹{v.CurrentPrice}</div>
                          )}
                        </div>
                      )
                    },
                    {
                      title: 'STOCK',
                      dataIndex: 'quantity',
                      key: 'stock',
                      align: 'center',
                      render: (qty) => (
                        <Tag color={qty > 5 ? 'success' : (qty > 0 ? 'warning' : 'error')} style={{ borderRadius: 4, minWidth: 40, textAlign: 'center' }}>
                          {qty}
                        </Tag>
                      )
                    },
                    {
                      title: 'STATUS',
                      key: 'status',
                      render: (v) => (
                        <Tag color={v.quantity > 0 ? 'processing' : 'default'} bordered={false} style={{ fontSize: 11 }}>
                          {v.quantity > 0 ? 'AVAILABLE' : 'OUT OF STOCK'}
                        </Tag>
                      )
                    }
                  ]}
                />
              </div>
            ),
            rowExpandable: (record) => record.variants?.length > 0,
          }}
        />
      </div>

      {/* ─── Product View Details Modal ─── */}
      <Modal
        open={!!viewProduct}
        onCancel={() => setViewProduct(null)}
        footer={null}
        width={800}
        centered
        destroyOnClose
        className="product-view-modal"
        title={null}
        styles={{ body: { padding: 0 } }}
      >
        {viewProduct && (
          <div style={{ display: 'flex', gap: 0, minHeight: 480, borderRadius: 8, overflow: 'hidden' }}>

            {/* ── Left: Image Gallery ── */}
            <div style={{ width: 320, flexShrink: 0, background: '#f8f9fa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', borderRight: '1px solid #f0f0f0' }}>
              {/* Discount badge */}
              {discount(viewProduct) > 0 && (
                <div style={{ position: 'absolute', top: 16, left: 16, background: '#e53935', color: '#fff', borderRadius: 4, padding: '3px 8px', fontSize: 12, fontWeight: 700, zIndex: 1 }}>
                  {discount(viewProduct)}% OFF
                </div>
              )}

              {/* Main image */}
              <div style={{ width: 240, height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {images.length > 0 ? (
                  <>
                    <img
                      src={`${BASEURL}${images[activeImg]?.url}`}
                      alt="product"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }}
                    />
                    {images.length > 1 && (
                      <>
                        <button onClick={prevImg} style={{ position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', fontSize: 12, color: '#374151' }}>
                          <LeftOutlined />
                        </button>
                        <button onClick={nextImg} style={{ position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', fontSize: 12, color: '#374151' }}>
                          <RightOutlined />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div style={{ width: 200, height: 200, background: '#e5e7eb', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 48 }}>
                    <InboxOutlined />
                  </div>
                )}
              </div>

              {/* Thumbnail strip */}
              {images.length > 1 && (
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {images.map((img, i) => (
                    <div key={i} onClick={() => setActiveImg(i)}
                      style={{ width: 52, height: 52, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', border: i === activeImg ? '2px solid #2874f0' : '2px solid #e0e0e0', transition: 'border-color 0.2s' }}>
                      <img src={`${BASEURL}${img.url}`} alt={`thumb-${i}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Image count */}
              {images.length > 0 && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
                  {activeImg + 1} / {images.length} photos
                </div>
              )}
            </div>

            {/* ── Right: Product Details ── */}
            <div style={{ flex: 1, padding: '28px 28px 24px', overflowY: 'auto', maxHeight: 560 }}>
              {/* Category chip */}
              <div style={{ marginBottom: 8 }}>
                <Tag color="blue" icon={<TagOutlined />} style={{ borderRadius: 50, fontWeight: 600, fontSize: 12 }}>
                  {getCategoryName(viewProduct.Category)}
                </Tag>
              </div>

              {/* Title */}
              <h2 style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 700, color: '#212121', margin: '0 0 16px', lineHeight: 1.3 }}>
                {viewProduct.title}
              </h2>

              {/* Price block */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20, padding: '16px 20px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                {viewProduct.variants?.length > 0 ? (
                  viewProduct.variants.map((v, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: i < viewProduct.variants.length - 1 ? '1px dashed #e2e8f0' : 'none', paddingBottom: i < viewProduct.variants.length - 1 ? 8 : 0 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{v.measurementUnit}</span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>{getUnitName(v.unit)}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>₹{v.offerPrice > 0 ? v.offerPrice : v.CurrentPrice}</div>
                        {v.offerPrice > 0 && v.offerPrice < v.CurrentPrice && (
                          <div style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'line-through', marginTop: -2 }}>₹{v.CurrentPrice}</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 24, fontWeight: 800, color: '#212121' }}>
                      ₹{viewProduct.offerPrice > 0 ? viewProduct.offerPrice : (viewProduct.CurrentPrice || '0')}
                    </span>
                    {viewProduct.offerPrice > 0 && viewProduct.offerPrice < viewProduct.CurrentPrice && (
                      <span style={{ fontSize: 16, color: '#9ca3af', textDecoration: 'line-through' }}>₹{viewProduct.CurrentPrice}</span>
                    )}
                  </div>
                )}
              </div>

              <Divider style={{ margin: '0 0 16px' }} />

              {/* Info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: 24 }}>
                {[
                  { icon: <InboxOutlined />, label: 'Stock Level', value: `${viewProduct.variants?.reduce((acc, v) => acc + (v.quantity || 0), 0) || 0} Total`, color: '#2874f0', bg: '#eff6ff' },
                  { icon: <PercentageOutlined />, label: 'Configurations', value: `${viewProduct.variants?.length || 0} Variants`, color: '#e53935', bg: '#fef2f2' },
                  { icon: <TagOutlined />, label: 'GST Rate', value: `${viewProduct.gst || 0}%`, color: '#059669', bg: '#ecfdf5' },
                  { icon: <AppstoreOutlined />, label: 'Category', value: getCategoryName(viewProduct.Category), color: '#d97706', bg: '#fffbeb' },
                ].map(({ icon, label, value, color, bg }) => (
                  <div key={label} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', background: bg, borderRadius: 10, border: `1px solid ${bg}` }}>
                    <div style={{ color, fontSize: 18, background: '#fff', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>{icon}</div>
                    <div>
                      <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
                      <div style={{ fontWeight: 700, color: '#1f2937', fontSize: 13 }}>{value}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Variants Section */}
              {viewProduct.variants && viewProduct.variants.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <ShoppingOutlined style={{ color: '#2874f0' }} />
                    <span style={{ fontWeight: 700, color: '#374151', fontSize: 14 }}>Product Configurations</span>
                  </div>
                  <div style={{ border: '1px solid #f0f3f6', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead style={{ background: '#f8fafc', borderBottom: '1px solid #f0f3f6' }}>
                        <tr>
                          <th style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Unit</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Size</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Price</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewProduct.variants.map((v, i) => (
                          <tr key={i} style={{ borderBottom: i < viewProduct.variants.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                            <td style={{ padding: '12px 16px', color: '#1e293b', fontWeight: 500 }}>{getUnitName(v.unit)}</td>
                            <td style={{ padding: '12px 16px', color: '#475569' }}>{v.measurementUnit}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: '#0f172a' }}>₹{v.offerPrice > 0 ? v.offerPrice : v.CurrentPrice}</div>
                              {v.offerPrice > 0 && v.CurrentPrice > 0 && v.offerPrice < v.CurrentPrice && (
                                <div style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'line-through' }}>₹{v.CurrentPrice}</div>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <Tag color={v.quantity > 5 ? 'success' : (v.quantity > 0 ? 'warning' : 'error')} style={{ borderRadius: 4, margin: 0 }}>
                                {v.quantity}
                              </Tag>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Description */}
              {viewProduct.description && (
                <>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Description
                  </div>
                  <div style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.7, padding: '12px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f0f0f0', marginBottom: 16 }}>
                    {viewProduct.description}
                  </div>
                </>
              )}

              {/* Tags / Highlights */}
              {viewProduct.tags?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: 13 }}>Tags</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {viewProduct.tags.map((tag, i) => (
                      <Tag key={i} icon={<StarOutlined />} color="geekblue" style={{ borderRadius: 50 }}>{tag}</Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer actions */}
              <Divider style={{ margin: '16px 0 16px' }} />
              <div style={{ display: 'flex', gap: 10 }}>
                <Button icon={<EditOutlined />} type="primary"
                  onClick={() => { setViewProduct(null); navigate('/admin/add-product', { state: { product: viewProduct, isEdit: true } }); }}
                  style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
                  Edit Product
                </Button>
                <Button danger icon={<DeleteOutlined />} onClick={() => { setViewProduct(null); handleDelete(viewProduct._id); }}
                  style={{ borderRadius: 4, fontWeight: 600 }}>
                  Delete
                </Button>
                <Button onClick={() => setViewProduct(null)} style={{ marginLeft: 'auto', borderRadius: 4 }}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Upload/Update Products Modal */}
      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>Bulk Ingest & Update Products</span>}
        open={showUploadModal} onCancel={() => setShowUploadModal(false)} footer={null} centered width={560}
      >
        <div style={{ marginTop: 16 }}>
          <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.5 }}>
            Upload an Excel (.xlsx or .xls) file containing product detail definitions. You can create brand new products OR bulk update existing ones by specifying their DB `id` or matching by their `title`.
          </p>

          <Button
            type="dashed"
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
            style={{ width: '100%', marginBottom: 20, height: 40, color: '#4f46e5', borderColor: '#4f46e5', borderRadius: 6, fontWeight: 600 }}
          >
            Download Products Excel Template
          </Button>

          <div style={{ border: '2px dashed #cbd5e1', borderRadius: 8, padding: '24px 16px', textAlign: 'center', background: '#f8fafc', marginBottom: 20 }}>
            <UploadOutlined style={{ fontSize: 32, color: '#64748b', marginBottom: 12 }} />
            <div style={{ marginBottom: 12 }}>
              <Upload
                beforeUpload={(file) => {
                  setFile(file);
                  setUploadReport(null);
                  return false; // prevent automatic upload
                }}
                fileList={file ? [file] : []}
                onRemove={() => setFile(null)}
                accept=".xlsx, .xls"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Excel File</Button>
              </Upload>
            </div>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>Only .xlsx and .xls files are supported</span>
          </div>

          {uploadReport && (
            <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#1e293b', fontWeight: 600, fontSize: 14 }}>Ingestion Summary Report</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 13 }}>
                <div>Total Processed Products: <strong style={{ color: '#0f172a' }}>{uploadReport.totalProcessed}</strong></div>
                <div>Created Products: <strong style={{ color: '#16a34a' }}>{uploadReport.createdCount}</strong></div>
                <div>Updated Products: <strong style={{ color: '#2563eb' }}>{uploadReport.updatedCount}</strong></div>
                <div>Synced Variants & Stock: <strong style={{ color: '#d97706' }}>{uploadReport.activeVariantsSaved}</strong></div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowUploadModal(false)}>Close</Button>
            <Button
              type="primary"
              onClick={handleBulkUpload}
              loading={uploadLoading}
              disabled={!file}
              style={{ background: '#4f46e5', borderColor: '#4f46e5', borderRadius: 4, fontWeight: 600 }}
            >
              Upload & Process Ingestion
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProductList;

